// Re:Focus — Utility Functions
// Pure functions with no app state dependencies

// Legacy status values → canonical 3-state (Active, Waiting, Done)
const LEGACY_STATUS_MAP = {
  // Sprint 2 disposition model → reverted 3-state
  'Ripe': 'Active', 'Future Now': 'Active', 'Delegate': 'Active',
  // Granular legacy → 3-state
  'In progress': 'Active', 'In Progress': 'Active', 'Not started': 'Active',
  'Blocked': 'Waiting', 'On Hold': 'Waiting', 'Waiting For': 'Waiting',
  'Provisionally Complete': 'Done', 'Completed': 'Done', 'Confirmed': 'Done',
  'Preliminary Design': 'Active', 'Design': 'Active',
  'Pre Production': 'Active', 'In Production': 'Active',
};

// XSS guard — escape all user content before injecting into innerHTML
export function e(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Due date urgency — strict 3 states: overdue / soon / later
export function getDueDateUrgency(dateStr) {
  if (!dateStr) return 'later';
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(dateStr + 'T00:00:00');
  const diff = Math.round((due - today) / 86400000);
  if (diff <= 0) return 'overdue';
  if (diff <= 7) return 'soon';
  return 'later';
}

// Convert URLs to anchor tags (escapes text first)
export function linkify(text) {
  return e(text).replace(/(https?:\/\/[^\s<>"]+)/g, '<a href="$1" target="_blank" rel="noopener" class="note-link">$1</a>');
}

// Context tag parser — strips @tag from task text
export function parseContext(text) {
  const str = String(text || '');
  // New format: "task text @context" (tag at end)
  const endMatch = str.match(/^(.*?)\s+@(\w+)$/);
  if (endMatch) return { context: endMatch[2].toLowerCase(), displayText: endMatch[1] };
  // Legacy format: "@context task text" (tag at start)
  const startMatch = str.match(/^@(\w+)\s+([\s\S]*)/);
  if (startMatch) return { context: startMatch[1].toLowerCase(), displayText: startMatch[2] };
  return { context: null, displayText: str };
}

// Short date formatter used across card face, panel, and tasks
export function formatShortDate(dateStr) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  try { const d = new Date(dateStr + 'T00:00:00'); return `${months[d.getMonth()]} ${d.getDate()}`; }
  catch { return dateStr; }
}

export function formatRelativeTime(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' min ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + ' hour' + (hrs !== 1 ? 's' : '') + ' ago';
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  return days + ' days ago';
}

export function formatCapDate(isoStr) {
  const d = new Date(isoStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function formatTaskDate(isoStr) {
  const d = new Date(isoStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

export function formatTaskDuePill(dueDateStr) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const due = new Date(dueDateStr + 'T00:00:00');
  const label = `${months[due.getMonth()]} ${due.getDate()}`;
  const urgency = getDueDateUrgency(dueDateStr);
  return `<span class="task-due-pill urgency-${urgency}">${urgency === 'overdue' ? 'Overdue' : label}</span>`;
}

// P2-8: Distinct confidence glyphs
export function confidenceGlyph(level) {
  if (level === 'high') return '\u25c9';   // ◉
  if (level === 'medium') return '\u25ce'; // ◎
  return '\u25cb';                         // ○
}

export function isCapRecent(isoStr) {
  return (Date.now() - new Date(isoStr).getTime()) < 86400000;
}

export function normalizeStatus(s) { return LEGACY_STATUS_MAP[s] || s; }
